import * as THREE from 'three';
import { TextureLoader } from 'three';
import {
  getTexturePreviewUrl,
  MAP_BACKUP_USERDATA_KEY,
  MAP_PREVIEW_URLS_USERDATA_KEY,
  MAP_VISIBILITY_USERDATA_KEY,
  resolveMapOnMaterial,
  ensureMapUserData,
  setMaterialMapVisibility,
  configureMaterialMapTexture,
  getMaterialMapUndoUrl,
  setMapPreviewUrl,
} from './materialMaps';
import {
  MATERIAL_PANEL_LEGACY_UI_KEYS,
  MATERIAL_PANEL_SECTIONS,
  MATERIAL_PROPERTY_LABELS,
  snapshotMapScalars,
  applyMapScalars,
  EDITOR_MAP_SCALARS_KEY,
} from './materialPanelSchema';

export function isTextureMapKey(key) {
  return (
    key === 'map' ||
    key === 'matcap' ||
    key === 'gradientMap' ||
    key.endsWith('Map')
  );
}

/** Keys that must not appear in the dynamic editor (internal / non-serializable). */
const SKIP_PROPERTY_KEYS = new Set([
  'uuid',
  'type',
  'isMaterial',
  'version',
  'userData',
  'defines',
  'extensions',
  'glslVersion',
  'onBeforeCompile',
  'onBeforeRender',
  'customProgramCacheKey',
  'uniformsGroups',
  'lights',
  'clippingPlanes',
  'clipIntersection',
  'clipShadows',
]);

const SIDE_OPTIONS = [
  { value: THREE.FrontSide, label: 'Front' },
  { value: THREE.BackSide, label: 'Back' },
  { value: THREE.DoubleSide, label: 'Double' },
];

const BLENDING_OPTIONS = [
  { value: THREE.NoBlending, label: 'NoBlending' },
  { value: THREE.NormalBlending, label: 'Normal' },
  { value: THREE.AdditiveBlending, label: 'Additive' },
  { value: THREE.SubtractiveBlending, label: 'Subtractive' },
  { value: THREE.MultiplyBlending, label: 'Multiply' },
];

function isWritable(material, key) {
  if (SKIP_PROPERTY_KEYS.has(key)) return false;
  let proto = material;
  while (proto) {
    const desc =
      Object.getOwnPropertyDescriptor(proto, key) ||
      Object.getOwnPropertyDescriptor(Object.getPrototypeOf(proto) || {}, key);
    if (desc) {
      if (desc.get && !desc.set) return false;
      return desc.writable !== false || !!desc.set;
    }
    proto = Object.getPrototypeOf(proto);
  }
  return key in material;
}

function collectPrototypeKeys(material) {
  const keys = new Set();
  let proto = material;
  while (proto && proto !== Object.prototype) {
    Object.getOwnPropertyNames(proto).forEach((k) => {
      if (!k.startsWith('_') && k !== 'constructor') keys.add(k);
    });
    proto = Object.getPrototypeOf(proto);
  }
  Object.keys(material).forEach((k) => keys.add(k));
  return [...keys].sort();
}

function numberRangeForKey(key) {
  const k = key.toLowerCase();
  if (
    k.includes('opacity') ||
    k === 'metalness' ||
    k === 'roughness' ||
    k === 'alphatest' ||
    k.includes('intensity') && !k.includes('map')
  ) {
    return { min: 0, max: k.includes('opacity') || k === 'metalness' || k === 'roughness' || k === 'alphatest' ? 1 : 5, step: 0.01 };
  }
  if (k === 'shininess') return { min: 0, max: 200, step: 1 };
  if (k.includes('linewidth')) return { min: 0, max: 20, step: 0.1 };
  return { min: -1000, max: 1000, step: 0.01 };
}

export function serializeMaterialPropertyValue(material, key) {
  if (isTextureMapKey(key)) {
    const { texture, enabled, previewUrl } = resolveMapOnMaterial(material, key);
    const url =
      getMaterialMapUndoUrl(material, key) ||
      previewUrl ||
      (texture ? getTexturePreviewUrl(texture) : null);
    if (url) setMapPreviewUrl(material, key, url);
    return {
      kind: 'texture',
      visibility: enabled,
      data: url,
      previewUrl: url,
    };
  }

  if (key === 'opacity') {
    return {
      kind: 'number',
      data: typeof material.opacity === 'number' ? material.opacity : 1,
    };
  }
  if (key === 'transparent') {
    return { kind: 'boolean', data: !!material.transparent };
  }

  const value = material[key];
  if (value === null && (key.endsWith('Map') || key === 'map')) {
    return { kind: 'texture', data: null };
  }
  if (value?.isTexture) {
    const url =
      getMaterialMapUndoUrl(material, key) || getTexturePreviewUrl(value);
    if (url) setMapPreviewUrl(material, key, url);
    return { kind: 'texture', data: url, previewUrl: url };
  }
  if (value?.isColor) {
    return { kind: 'color', data: `#${value.getHexString()}` };
  }
  if (value?.isVector2) {
    return { kind: 'vector2', data: { x: value.x, y: value.y } };
  }
  if (value?.isVector3) {
    return { kind: 'vector3', data: { x: value.x, y: value.y, z: value.z } };
  }
  if (typeof value === 'number') {
    return { kind: 'number', data: value };
  }
  if (typeof value === 'boolean') {
    return { kind: 'boolean', data: value };
  }
  if (typeof value === 'string') {
    return { kind: 'string', data: value };
  }
  return null;
}

/** Always surfaced first — critical for GLB export and often missed by prototype walks. */
const PRIORITY_MATERIAL_KEYS = [
  'opacity',
  'transparent',
  'alphaTest',
  'depthWrite',
  'visible',
  'side',
  'color',
];

export function describeMaterialProperty(material, key) {
  if (!material) return null;

  if (key === 'opacity') {
    const opacity =
      typeof material.opacity === 'number' ? material.opacity : 1;
    return {
      key: 'opacity',
      kind: 'number',
      value: opacity,
      min: 0,
      max: 1,
      step: 0.01,
    };
  }

  if (key === 'transparent') {
    return {
      key: 'transparent',
      kind: 'boolean',
      value: !!material.transparent,
    };
  }

  if (isTextureMapKey(key)) {
    const { texture, enabled, previewUrl } = resolveMapOnMaterial(material, key);
    return {
      key,
      kind: 'texture',
      texture: texture || null,
      previewUrl: previewUrl || (texture ? getTexturePreviewUrl(texture) : null),
      hasTexture: !!(texture || previewUrl),
      mapEnabled: enabled,
    };
  }

  if (!isWritable(material, key)) return null;
  const value = material[key];
  if (value === undefined && !(key in material)) return null;

  if (value?.isTexture) {
    return {
      key,
      kind: 'texture',
      texture: value,
      previewUrl: getTexturePreviewUrl(value),
      hasTexture: true,
      mapEnabled: true,
    };
  }
  if (value?.isColor) {
    return {
      key,
      kind: 'color',
      value: `#${value.getHexString()}`,
    };
  }
  if (value?.isVector2) {
    return {
      key,
      kind: 'vector2',
      value: { x: value.x, y: value.y },
    };
  }
  if (value?.isVector3) {
    return {
      key,
      kind: 'vector3',
      value: { x: value.x, y: value.y, z: value.z },
    };
  }
  if (typeof value === 'number') {
    if (key === 'side') {
      return { key, kind: 'side', value };
    }
    if (key === 'blending') {
      return { key, kind: 'blending', value };
    }
    const range = numberRangeForKey(key);
    return { key, kind: 'number', value, ...range };
  }
  if (typeof value === 'boolean') {
    return { key, kind: 'boolean', value };
  }
  if (typeof value === 'string') {
    return { key, kind: 'string', value };
  }
  return null;
}

function getSchemaKeySet() {
  const keys = new Set(MATERIAL_PANEL_LEGACY_UI_KEYS);
  MATERIAL_PANEL_SECTIONS.forEach((section) => {
    section.keys.forEach((key) => keys.add(key));
  });
  return keys;
}

export function listPresetMaterialPanelSections(material) {
  if (!material) return [];

  const sections = MATERIAL_PANEL_SECTIONS.map((section) => {
    const fields = [];
    section.keys.forEach((key) => {
      if (MATERIAL_PANEL_LEGACY_UI_KEYS.has(key)) return;
      const desc = describeMaterialProperty(material, key);
      if (!desc) return;
      fields.push({
        ...desc,
        label: MATERIAL_PROPERTY_LABELS[key] || key,
      });
    });
    return { ...section, fields };
  }).filter((section) => section.fields.length > 0);

  const schemaKeys = getSchemaKeySet();
  const otherFields = listMaterialPropertyDescriptors(material).filter(
    (desc) =>
      desc?.key &&
      !schemaKeys.has(desc.key) &&
      !isTextureMapKey(desc.key) &&
      !desc.key.startsWith('uniform:')
  );

  if (otherFields.length > 0) {
    sections.push({
      id: 'other',
      title: 'Other',
      fields: otherFields.map((desc) => ({
        ...desc,
        label: MATERIAL_PROPERTY_LABELS[desc.key] || desc.key,
      })),
    });
  }

  return sections;
}

export function listMaterialPropertyDescriptors(material) {
  if (!material) return [];
  const byKey = new Map();

  PRIORITY_MATERIAL_KEYS.forEach((key) => {
    const desc = describeMaterialProperty(material, key);
    if (desc) byKey.set(key, desc);
  });

  collectPrototypeKeys(material).forEach((key) => {
    if (byKey.has(key)) return;
    const desc = describeMaterialProperty(material, key);
    if (desc) byKey.set(key, desc);
  });

  return Array.from(byKey.values());
}

export function listShaderUniformDescriptors(material) {
  if (!material?.isShaderMaterial || !material.uniforms) return [];
  const out = [];
  Object.entries(material.uniforms).forEach(([name, uniform]) => {
    if (!uniform || uniform.value === undefined) return;
    const v = uniform.value;
    if (v?.isTexture) {
      out.push({
        key: `uniform:${name}`,
        uniformName: name,
        kind: 'texture',
        texture: v,
        previewUrl: getTexturePreviewUrl(v),
        hasTexture: true,
        mapEnabled: true,
      });
    } else if (v?.isColor) {
      out.push({
        key: `uniform:${name}`,
        uniformName: name,
        kind: 'color',
        value: `#${v.getHexString()}`,
      });
    } else if (typeof v === 'number') {
      out.push({
        key: `uniform:${name}`,
        uniformName: name,
        kind: 'number',
        value: v,
        min: 0,
        max: 10,
        step: 0.01,
      });
    } else if (typeof v === 'boolean') {
      out.push({
        key: `uniform:${name}`,
        uniformName: name,
        kind: 'boolean',
        value: v,
      });
    } else if (v?.isVector2) {
      out.push({
        key: `uniform:${name}`,
        uniformName: name,
        kind: 'vector2',
        value: { x: v.x, y: v.y },
      });
    } else if (v?.isVector3) {
      out.push({
        key: `uniform:${name}`,
        uniformName: name,
        kind: 'vector3',
        value: { x: v.x, y: v.y, z: v.z },
      });
    }
  });
  return out;
}

function markMaterialEdited(mesh) {
  if (!mesh) return;
  mesh.userData = mesh.userData || {};
  mesh.userData.threeMaterialEdited = true;
  let p = mesh.parent;
  while (p) {
    p.userData = p.userData || {};
    p.userData.threeMaterialEdited = true;
    p = p.parent;
  }
}

/** Nearest publishable mesh asset root (GLB pivot / shape group). */
function resolveMaterialAssetRoot(mesh) {
  let current = mesh;
  while (current) {
    if (
      current.deleteType === 'mesh' ||
      current.objType === '3d' ||
      current.glb_type === 'shapes' ||
      current.userData?.isShapes === true ||
      current.isShapes === true
    ) {
      return current;
    }
    current = current.parent;
  }
  return mesh;
}

function childMeshSlotKey(mesh, root) {
  const parts = [];
  let node = mesh;
  while (node && node !== root) {
    parts.unshift(node.name || node.type || 'node');
    node = node.parent;
  }
  return parts.join('/') || mesh.name || mesh.type || 'mesh';
}

function listMeshSlotsInOrder(root) {
  const slots = [];
  if (!root?.traverse) return slots;
  root.traverse((obj) => {
    if (obj.isMesh || obj.isSkinnedMesh) slots.push(obj);
  });
  return slots;
}

function resolveExportRootForMaterials(root) {
  if (!root) return root;
  if (
    root.deleteType === 'mesh' &&
    Array.isArray(root.children) &&
    root.children.length === 1 &&
    root.children[0]?.traverse
  ) {
    return root.children[0];
  }
  return root;
}

function snapshotMaterialOverride(mat) {
  if (!mat) return null;
  const ud = mat.userData || {};
  const mapVisibility = ud[MAP_VISIBILITY_USERDATA_KEY] || {};
  const mapScalars = snapshotMapScalars(mat);
  return {
    wireframe: mat.wireframe === true,
    color: mat.color?.isColor ? `#${mat.color.getHexString()}` : undefined,
    emissive: mat.emissive?.isColor
      ? `#${mat.emissive.getHexString()}`
      : undefined,
    emissiveIntensity:
      typeof mat.emissiveIntensity === 'number'
        ? mat.emissiveIntensity
        : undefined,
    metalness: typeof mat.metalness === 'number' ? mat.metalness : undefined,
    roughness: typeof mat.roughness === 'number' ? mat.roughness : undefined,
    opacity: typeof mat.opacity === 'number' ? mat.opacity : undefined,
    transparent: !!mat.transparent,
    side: mat.side,
    blending: mat.blending,
    depthWrite: mat.depthWrite,
    flatShading: mat.flatShading === true,
    materialType: mat.type,
    // Same uuid ⇒ children shared this material in the editor; re-link on load.
    sharedMaterialId: mat.uuid,
    // Remember map visibility so solid color edits survive skinned republish.
    mapVisibility: { ...mapVisibility },
    mapHidden: mat.map == null && !!ud[MAP_BACKUP_USERDATA_KEY]?.map,
    // Map intensity / scale (lightMapIntensity, bumpScale, …) — not glTF-native.
    ...(mapScalars ? { mapScalars, [EDITOR_MAP_SCALARS_KEY]: mapScalars } : {}),
  };
}

function buildOverrideEntry(obj, exportRoot, index) {
  const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
  const namePath = childMeshSlotKey(obj, exportRoot);
  const base = {
    index,
    name: obj.name || '',
    namePath,
    isSkinnedMesh: !!obj.isSkinnedMesh,
  };
  if (mats.length === 1) {
    return { ...base, ...snapshotMaterialOverride(mats[0]) };
  }
  return {
    ...base,
    materials: mats.map((m) => snapshotMaterialOverride(m)).filter(Boolean),
  };
}

/**
 * Persist per-child live material state on the asset root.
 * Animation/skinned GLBs cannot safely re-export the binary — child colors/wireframe
 * must ride in material_settings.childOverrides and be re-applied on load.
 */
export function syncChildMaterialOverridesToAssetRoot(mesh) {
  const root = resolveMaterialAssetRoot(mesh);
  if (!root) return null;

  const exportRoot = resolveExportRootForMaterials(root);
  const slots = listMeshSlotsInOrder(exportRoot);
  const overrides = {};

  const materialMatches = (a, b) => {
    if (!a || !b) return false;
    if (a === b) return true;
    const listA = Array.isArray(a) ? a : [a];
    const listB = Array.isArray(b) ? b : [b];
    if (listA.length !== listB.length) return false;
    return listA.every((mat, i) => mat === listB[i]);
  };

  slots.forEach((obj, index) => {
    if (!obj.material) return;
    // On live edits, only persist meshes that were actually changed.
    if (!obj.userData?.threeMaterialEdited) return;
    const entry = buildOverrideEntry(obj, exportRoot, index);
    overrides[`#${index}|${entry.namePath}`] = entry;

    // Siblings that still share this material instance must get the same
    // override — otherwise publish only restores the selected child.
    slots.forEach((sibling, siblingIndex) => {
      if (sibling === obj || !sibling.material) return;
      if (!materialMatches(sibling.material, obj.material)) return;
      sibling.userData = sibling.userData || {};
      sibling.userData.threeMaterialEdited = true;
      const siblingEntry = buildOverrideEntry(
        sibling,
        exportRoot,
        siblingIndex
      );
      overrides[`#${siblingIndex}|${siblingEntry.namePath}`] = siblingEntry;
    });
  });

  // Root edited with no mesh flags — capture full reconstruction.
  if (
    Object.keys(overrides).length === 0 &&
    root.userData?.threeMaterialEdited
  ) {
    slots.forEach((obj, index) => {
      if (!obj.material) return;
      const entry = buildOverrideEntry(obj, exportRoot, index);
      overrides[`#${index}|${entry.namePath}`] = entry;
    });
  }

  if (!root.userData || Object.isFrozen(root.userData)) {
    root.userData = { ...(root.userData || {}) };
  }
  const prev = root.userData.material_settings?.childOverrides || {};
  root.userData.material_settings = {
    ...(root.userData.material_settings || {}),
    // Merge so earlier child edits are not dropped when editing another child.
    childOverrides: { ...prev, ...overrides },
  };
  root.userData.threeMaterialEdited = true;
  return root.userData.material_settings.childOverrides;
}

/**
 * Full reconstruction of every child mesh/skinnedMesh material for publish.
 * Used especially for animation GLBs that must keep the original skin binary.
 */
export function collectChildMaterialOverrides(root) {
  if (!root) return {};

  const assetRoot = resolveMaterialAssetRoot(root);
  const exportRoot = resolveExportRootForMaterials(assetRoot);
  const slots = listMeshSlotsInOrder(exportRoot);
  const overrides = {};

  slots.forEach((obj, index) => {
    if (!obj.material) return;
    // Always snapshot every child on publish so reload matches the viewport.
    const entry = buildOverrideEntry(obj, exportRoot, index);
    const key = `#${index}|${entry.namePath}`;
    overrides[key] = entry;
  });

  // After a rename, userData (or material_settings) can be a frozen Redux/Immer
  // object, so writing directly throws. Clone any frozen layer before mutating.
  if (!assetRoot.userData || Object.isFrozen(assetRoot.userData)) {
    assetRoot.userData = { ...(assetRoot.userData || {}) };
  }
  assetRoot.userData.material_settings = {
    ...(assetRoot.userData.material_settings || {}),
    childOverrides: overrides,
  };
  if (Object.keys(overrides).length > 0) {
    assetRoot.userData.threeMaterialEdited =
      assetRoot.userData.threeMaterialEdited ||
      slots.some((s) => s.userData?.threeMaterialEdited);
  }

  return overrides;
}

function applyOverrideToMaterial(mat, override) {
  if (!mat || !override) return;
  if (override.wireframe !== undefined) {
    mat.wireframe = !!override.wireframe;
    if (override.wireframe) {
      mat.transparent = false;
      mat.alphaTest = 0;
      if (mat.opacity !== undefined) mat.opacity = 1;
      if (mat.depthWrite !== undefined) mat.depthWrite = true;
    }
  }
  if (override.color && mat.color?.set) {
    try {
      mat.color.set(override.color);
    } catch {
      mat.color.set(`#${override.color}`);
    }
    if (mat.userData?.originalMaterialProps) {
      mat.userData.originalMaterialProps.color = mat.color.getHex();
    }
  }
  if (override.emissive && mat.emissive?.set) {
    try {
      mat.emissive.set(override.emissive);
    } catch {
      /* ignore */
    }
  }
  if (typeof override.emissiveIntensity === 'number') {
    mat.emissiveIntensity = override.emissiveIntensity;
  }
  if (typeof override.metalness === 'number') mat.metalness = override.metalness;
  if (typeof override.roughness === 'number') mat.roughness = override.roughness;
  if (typeof override.opacity === 'number') mat.opacity = override.opacity;
  if (override.transparent !== undefined) mat.transparent = !!override.transparent;
  if (override.side !== undefined) mat.side = override.side;
  if (override.blending !== undefined) mat.blending = override.blending;
  if (override.depthWrite !== undefined) mat.depthWrite = override.depthWrite;
  if (override.flatShading !== undefined) mat.flatShading = !!override.flatShading;

  applyMapScalars(
    mat,
    override.mapScalars || override[EDITOR_MAP_SCALARS_KEY]
  );

  // If the editor hid the base map (solid color look), keep it hidden after reload.
  if (override.mapHidden && mat.map) {
    const ud = ensureMapUserData(mat);
    ud[MAP_BACKUP_USERDATA_KEY].map = mat.map;
    ud[MAP_VISIBILITY_USERDATA_KEY].map = false;
    mat.map = null;
  } else if (override.mapVisibility && typeof override.mapVisibility === 'object') {
    const ud = ensureMapUserData(mat);
    Object.entries(override.mapVisibility).forEach(([mapKey, visible]) => {
      if (typeof visible !== 'boolean') return;
      ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = visible;
      if (visible === false && mat[mapKey]) {
        ud[MAP_BACKUP_USERDATA_KEY][mapKey] = mat[mapKey];
        mat[mapKey] = null;
      }
    });
  }

  // Wireframe last: opacity/transparent from snapshot must not hide lines.
  if (mat.wireframe) {
    mat.transparent = false;
    mat.alphaTest = 0;
    mat.opacity = 1;
    if (mat.depthWrite !== undefined) mat.depthWrite = true;
    mat.userData = mat.userData || {};
    if (mat.color && mat.userData._wireframeColorBackup === undefined) {
      mat.userData._wireframeColorBackup = mat.color.getHex();
      const lum =
        mat.color.r * 0.2126 + mat.color.g * 0.7152 + mat.color.b * 0.0722;
      if (lum < 0.45 || mat.map) mat.color.setHex(0xffffff);
    }
  }

  mat.needsUpdate = true;
}

function resolveOverrideTargetMesh(slot, override, byIndex, byKey, byName) {
  if (typeof override?.index === 'number' && byIndex.has(override.index)) {
    return byIndex.get(override.index);
  }
  const indexMatch = String(slot).match(/^#(\d+)/);
  if (indexMatch) {
    const idx = Number(indexMatch[1]);
    if (byIndex.has(idx)) return byIndex.get(idx);
  }
  if (byKey.has(slot)) return byKey.get(slot);
  if (override?.namePath && byKey.has(override.namePath)) {
    return byKey.get(override.namePath);
  }
  const leaf = String(slot).includes('|')
    ? String(slot).split('|').pop()
    : String(slot).split('/').pop();
  if (leaf && byName.has(leaf)) return byName.get(leaf);
  if (override?.name && byName.has(override.name)) {
    return byName.get(override.name);
  }
  return null;
}

/** Re-apply per-child overrides after GLB load (skinned binaries keep original materials). */
export function resolveChildMaterialOverrides(settings) {
  if (!settings || typeof settings !== 'object') return null;
  if (settings.childOverrides && typeof settings.childOverrides === 'object') {
    return settings.childOverrides;
  }
  if (
    typeof settings.child_material_data === 'string' &&
    settings.child_material_data.trim()
  ) {
    try {
      const parsed = JSON.parse(settings.child_material_data);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Apply wireframe from compact `wireframe_mesh_indices` ("0,2,5") when full overrides are missing. */
export function applyWireframeMeshIndices(root, indicesStr) {
  if (!root || typeof indicesStr !== 'string' || !indicesStr.trim()) return;

  const indices = indicesStr
    .split(/[,\s]+/)
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isInteger(n) && n >= 0);
  if (!indices.length) return;

  const exportRoot = resolveExportRootForMaterials(root);
  const slots = listMeshSlotsInOrder(exportRoot);
  indices.forEach((index) => {
    const mesh = slots[index];
    if (!mesh?.material) return;
    mesh.userData = mesh.userData || {};
    mesh.userData.threeMaterialEdited = true;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((mat) => {
      if (!mat) return;
      applyOverrideToMaterial(mat, { wireframe: true });
    });
  });

  if (!root.userData || Object.isFrozen(root.userData)) {
    root.userData = { ...(root.userData || {}) };
  }
  root.userData.threeMaterialEdited = true;
  root.userData.material_settings = {
    ...(root.userData.material_settings || {}),
    wireframe_mesh_indices: indices.join(','),
  };
}

export function applyChildMaterialOverrides(root, overrides) {
  if (!root || !overrides || typeof overrides !== 'object') return;

  const exportRoot = resolveExportRootForMaterials(root);
  const slots = listMeshSlotsInOrder(exportRoot);
  const byIndex = new Map();
  const byKey = new Map();
  const byName = new Map();

  slots.forEach((obj, index) => {
    byIndex.set(index, obj);
    const key = childMeshSlotKey(obj, exportRoot);
    if (key) byKey.set(key, obj);
    byKey.set(`#${index}|${key}`, obj);
    const name = obj.name;
    if (name && !byName.has(name)) byName.set(name, obj);
  });

  // sharedMaterialId → canonical material instance (re-link siblings after load)
  const sharedMaterials = new Map();
  const applied = new Set();

  Object.entries(overrides).forEach(([slot, override]) => {
    if (!override || typeof override !== 'object') return;
    const mesh = resolveOverrideTargetMesh(
      slot,
      override,
      byIndex,
      byKey,
      byName
    );
    if (!mesh?.material) return;
    if (applied.has(mesh.uuid)) return;
    applied.add(mesh.uuid);

    mesh.userData = mesh.userData || {};
    mesh.userData.threeMaterialEdited = true;

    if (Array.isArray(mesh.material) && Array.isArray(override.materials)) {
      const next = mesh.material.slice();
      next.forEach((mat, i) => {
        const part = override.materials[i];
        if (!part) return;
        const sharedId = part.sharedMaterialId;
        if (sharedId && sharedMaterials.has(sharedId)) {
          next[i] = sharedMaterials.get(sharedId);
          return;
        }
        applyOverrideToMaterial(mat, part);
        if (sharedId) sharedMaterials.set(sharedId, mat);
      });
      mesh.material = next;
    } else if (Array.isArray(mesh.material)) {
      mesh.material.forEach((mat) => applyOverrideToMaterial(mat, override));
    } else {
      const sharedId = override.sharedMaterialId;
      if (sharedId && sharedMaterials.has(sharedId)) {
        mesh.material = sharedMaterials.get(sharedId);
      } else {
        applyOverrideToMaterial(mesh.material, override);
        if (sharedId) sharedMaterials.set(sharedId, mesh.material);
      }
    }
  });

  // Re-link any remaining meshes that still share an extras id from the GLB.
  relinkMeshesBySharedMaterialId(exportRoot);

  if (!root.userData || Object.isFrozen(root.userData)) {
    root.userData = { ...(root.userData || {}) };
  }
  root.userData.threeMaterialEdited = true;
  root.userData.material_settings = {
    ...(root.userData.material_settings || {}),
    childOverrides: overrides,
  };
}

/**
 * If several child meshes have the same editorSharedMaterialId extras (or the
 * same live material uuid recorded at publish), point them at one instance so
 * albedo edits keep updating all siblings after reload.
 */
export function relinkMeshesBySharedMaterialId(root) {
  if (!root?.traverse) return;
  const byId = new Map();

  root.traverse((obj) => {
    if ((!obj.isMesh && !obj.isSkinnedMesh) || !obj.material) return;
    if (Array.isArray(obj.material)) return;

    const mat = obj.material;
    const sharedId =
      mat.userData?.editorSharedMaterialId ||
      mat.userData?.sharedMaterialId ||
      null;
    if (!sharedId) return;

    if (byId.has(sharedId)) {
      obj.material = byId.get(sharedId);
    } else {
      byId.set(sharedId, mat);
    }
  });
}

export function applyMaterialPropertyValue(material, key, payload) {
  if (!material || !key) return;

  if (key.startsWith('uniform:')) {
    const uniformName = key.slice('uniform:'.length);
    const uniform = material.uniforms?.[uniformName];
    if (!uniform) return;
    applyUniformValue(uniform, payload);
    material.needsUpdate = true;
    return;
  }

  const { kind, data, previewUrl, visibility, clear } = payload;

  if (kind === 'texture') {
    if (visibility !== undefined) {
      const ud = ensureMapUserData(material);
      const backups = ud[MAP_BACKUP_USERDATA_KEY];
      const texture = material[key] || backups[key];
      if (!texture) return;

      backups[key] = texture;
      const preview = getTexturePreviewUrl(texture);
      if (preview) ud[MAP_PREVIEW_URLS_USERDATA_KEY][key] = preview;
      ud[MAP_VISIBILITY_USERDATA_KEY][key] = visibility;
      material[key] = visibility ? texture : null;
      material.needsUpdate = true;
      return;
    }

    if (clear) {
      const old = material[key];
      const backups = material.userData?.[MAP_BACKUP_USERDATA_KEY];
      if (old?.isTexture) old.dispose();
      if (backups?.[key]) {
        backups[key].dispose();
        delete backups[key];
      }
      material[key] = null;
      material.needsUpdate = true;
      return;
    }

    if (data || previewUrl) {
      const url = data || previewUrl;
      const loader = new TextureLoader();
      loader.load(url, (texture) => {
        const ud = ensureMapUserData(material);
        const backups = ud[MAP_BACKUP_USERDATA_KEY];
        const old = material[key] || backups[key];
        configureMaterialMapTexture(texture, key, old);
        // Keep previous texture alive so undo can restore without re-fetch when URL is missing.
        backups[key] = texture;
        ud[MAP_VISIBILITY_USERDATA_KEY][key] = true;
        if (previewUrl || url) {
          ud[MAP_PREVIEW_URLS_USERDATA_KEY][key] = previewUrl || url;
        }
        material[key] = texture;
        if (
          key === 'map' &&
          material.color?.isColor &&
          material.color.getHex() === 0x000000
        ) {
          material.color.set(0xffffff);
        }
        material.needsUpdate = true;
      });
    }
    return;
  }

  if (kind === 'color' && material[key]?.isColor) {
    material[key].set(data);
    if (key === 'color' && material.userData?.originalMaterialProps) {
      material.userData.originalMaterialProps.color = material.color.getHex();
    }
    if (key === 'emissive' && material.userData?.originalMaterialProps) {
      material.userData.originalMaterialProps.emissive = material.emissive.getHex();
    }
  } else if (kind === 'vector2' && material[key]?.isVector2) {
    material[key].set(data.x, data.y);
  } else if (kind === 'vector3' && material[key]?.isVector3) {
    material[key].set(data.x, data.y, data.z);
  } else if (kind === 'number') {
    material[key] = data;
    if (key === 'opacity' && typeof data === 'number' && data < 1 - 1e-6) {
      material.transparent = true;
      if (material.depthWrite !== undefined) {
        material.depthWrite = false;
      }
    }
  } else if (kind === 'boolean') {
    material[key] = data;
    if (key === 'transparent' && data && material.depthWrite !== undefined) {
      const op =
        typeof material.opacity === 'number' ? material.opacity : 1;
      if (op < 1) material.depthWrite = false;
    }
    if (key === 'wireframe') {
      if (data) {
        // Same visibility path as Rendering Mode → Wireframe.
        material.transparent = false;
        material.alphaTest = 0;
        if (material.opacity !== undefined) material.opacity = 1;
        if (material.depthWrite !== undefined) material.depthWrite = true;
        // Local brighten without importing materialFunction (avoid cycles).
        material.userData = material.userData || {};
        if (
          material.color &&
          material.userData._wireframeColorBackup === undefined
        ) {
          material.userData._wireframeColorBackup = material.color.getHex();
          const lum =
            material.color.r * 0.2126 +
            material.color.g * 0.7152 +
            material.color.b * 0.0722;
          if (lum < 0.45 || material.map) {
            material.color.setHex(0xffffff);
          }
        }
        if (
          material.emissive &&
          material.userData._wireframeEmissiveBackup === undefined
        ) {
          material.userData._wireframeEmissiveBackup =
            material.emissive.getHex();
          material.userData._wireframeEmissiveIntensityBackup =
            typeof material.emissiveIntensity === 'number'
              ? material.emissiveIntensity
              : 1;
          material.emissive.setHex(0xffffff);
          material.emissiveIntensity = Math.max(
            material.emissiveIntensity || 0,
            0.35
          );
        }
      } else {
        const ud = material.userData;
        if (ud?._wireframeColorBackup !== undefined && material.color) {
          try {
            material.color.setHex(ud._wireframeColorBackup);
          } catch {
            /* ignore */
          }
          delete ud._wireframeColorBackup;
        }
        if (ud?._wireframeEmissiveBackup !== undefined && material.emissive) {
          try {
            material.emissive.setHex(ud._wireframeEmissiveBackup);
          } catch {
            /* ignore */
          }
          delete ud._wireframeEmissiveBackup;
        }
        if (
          ud?._wireframeEmissiveIntensityBackup !== undefined &&
          material.emissiveIntensity !== undefined
        ) {
          material.emissiveIntensity = ud._wireframeEmissiveIntensityBackup;
          delete ud._wireframeEmissiveIntensityBackup;
        }
      }
    }
  } else if (kind === 'string') {
    material[key] = data;
  } else if (kind === 'side') {
    material.side = data;
  } else if (kind === 'blending') {
    material.blending = data;
  }

  material.needsUpdate = true;
}

function applyUniformValue(uniform, payload) {
  const { kind, data } = payload;
  const v = uniform.value;
  if (kind === 'color' && v?.isColor) {
    v.set(data);
  } else if (kind === 'vector2' && v?.isVector2) {
    v.set(data.x, data.y);
  } else if (kind === 'vector3' && v?.isVector3) {
    v.set(data.x, data.y, data.z);
  } else if (kind === 'number' || kind === 'boolean') {
    uniform.value = data;
  } else if (kind === 'texture' && v?.isTexture) {
    if (!data) {
      v.dispose?.();
      uniform.value = null;
    } else {
      new TextureLoader().load(data, (tex) => {
        uniform.value = tex;
      });
    }
  }
}

export function getMeshPathLabel(mesh, root) {
  const parts = [];
  let cur = mesh;
  while (cur && cur !== root?.parent) {
    parts.unshift(cur.name || cur.type || 'Object');
    if (cur === root) break;
    cur = cur.parent;
  }
  return parts.join(' › ') || mesh.name || 'Mesh';
}

/** Asset types that must never show the Material component. */
const MATERIAL_EXCLUDED_DELETE_TYPES = new Set([
  'text',
  'video',
  'audio',
  'hotspot',
  'particle',
  'light',
  'Environment',
]);

/** Primitive / library shape (cube, sphere, star group, etc.). */
export function isShapeAsset(root) {
  if (!root) return false;
  return (
    root.glb_type === 'shapes' ||
    root.userData?.glb_type === 'shapes' ||
    root.isShapes === true ||
    root.userData?.isShapes === true
  );
}

/** Shape asset root (group) from self or any ancestor. */
export function findShapeAssetRoot(root) {
  if (!root) return null;
  let current = root;
  while (current) {
    if (isShapeAsset(current)) return current;
    current = current.parent;
  }
  return null;
}

/** Image plane asset in the scene. */
export function isImageAsset(root) {
  if (!root) return false;
  if (root.deleteType === 'image' || root.type === 'Image') return true;
  return getInheritedDeleteType(root) === 'image';
}

/** Image asset root from self or any ancestor. */
export function findImageAssetRoot(root) {
  if (!root) return null;
  let current = root;
  while (current) {
    if (isImageAsset(current)) return current;
    current = current.parent;
  }
  return null;
}

/** Nearest deleteType walking up the hierarchy (text mesh child, GLB pivot, etc.). */
export function getInheritedDeleteType(root) {
  let current = root;
  while (current) {
    if (current.deleteType) return current.deleteType;
    current = current.parent;
  }
  return null;
}

/** glb_type from self or an ancestor (GLB pivot, shape group, etc.). */
export function getGlbTypeFromHierarchy(root) {
  let current = root;
  while (current) {
    const gt = current.glb_type ?? current.userData?.glb_type;
    if (gt) return gt;
    current = current.parent;
  }
  return null;
}

/**
 * GLB / OBJ / FBX mesh pivot (deleteType mesh), or walk up from a mesh-extracted child.
 * Excludes shapes, PLY, and non-3D asset types.
 */
export function findGlbMeshAssetRoot(root) {
  if (!root) return null;
  let current = root;
  while (current) {
    // Prefer the editor GLB pivot — never stop on an extracted child mesh,
    // so the material list always covers the whole model.
    if (current.deleteType === 'mesh') {
      const gt = current.glb_type ?? current.userData?.glb_type ?? 'normal';
      if (gt === 'ply' || gt === 'shapes') return null;
      return current;
    }
    current = current.parent;
  }
  return null;
}

export function isGlbAssetSelection(root) {
  return !!findGlbMeshAssetRoot(root);
}

/** Root used by the material list (GLB → shapes → images). */
export function findMaterialListAssetRoot(root) {
  return (
    findGlbMeshAssetRoot(root) ||
    findShapeAssetRoot(root) ||
    findImageAssetRoot(root) ||
    null
  );
}

/** GLB / asset root (Group pivot) — not a mesh; no per-property material panel. */
export function isParentAssetSelection(root) {
  if (!root) return false;
  if (root.isMesh || root.isSkinnedMesh) return false;
  if (isShapeAsset(root)) return false;
  if (!findGlbMeshAssetRoot(root)) return false;
  return (
    root.type === 'Group' ||
    root.type === 'Scene' ||
    root.type === 'Object3D' ||
    root.objType === '3d' ||
    root.deleteType === 'mesh'
  );
}

/** Child mesh selection — mesh with a material slot (not sufficient alone for panel visibility). */
export function shouldShowMaterialPropertiesPanel(root) {
  if (!root) return false;
  return !!(root.isMesh || root.isSkinnedMesh);
}

/**
 * Material component: GLB (+ children), shapes, and images only.
 * Not text, video, audio, hotspots, particles, PLY, etc.
 */
export function shouldShowMaterialComponent(root) {
  if (!root) return false;

  const inheritedDeleteType = getInheritedDeleteType(root);
  if (
    inheritedDeleteType &&
    MATERIAL_EXCLUDED_DELETE_TYPES.has(inheritedDeleteType)
  ) {
    return false;
  }

  const glbType = getGlbTypeFromHierarchy(root);
  if (glbType === 'ply') return false;

  if (isImageAsset(root)) return true;
  if (isShapeAsset(root)) return true;
  if (isGlbAssetSelection(root)) return true;

  return false;
}

/** Full material property editor (mesh/shape/image — not GLB parent placeholder). */
export function shouldShowFullMaterialEditor(root) {
  if (!shouldShowMaterialComponent(root)) return false;
  if (isParentAssetSelection(root)) return false;
  return (
    shouldShowMaterialPropertiesPanel(root) ||
    isShapeAsset(root) ||
    isImageAsset(root)
  );
}

function pushMeshMaterialEntries(mesh, root, entries, seen) {
  if (!mesh?.isMesh && !mesh?.isSkinnedMesh) return;

  const materials = Array.isArray(mesh.material)
    ? mesh.material
    : [mesh.material];
  const pathLabel = getMeshPathLabel(mesh, root);

  materials.forEach((material, materialIndex) => {
    if (!material) return;

    const id = `${mesh.uuid}:${material.uuid}:${materialIndex}`;
    if (seen.has(id)) return;
    seen.add(id);

    const slotSuffix =
      materials.length > 1 ? ` · material ${materialIndex}` : '';

    entries.push({
      id,
      mesh,
      meshUuid: mesh.uuid,
      materialIndex,
      material,
      materialUuid: material.uuid,
      materialType: material.type,
      label: `${pathLabel}${slotSuffix}`,
      pathLabel,
      scope: root === mesh ? 'self' : 'descendants',
    });
  });
}

/** Material slots for the selected mesh, or all meshes under a shape group. */
export function collectMaterialEntries(root) {
  if (!root) return [];

  const entries = [];
  const seen = new Set();

  if (root.isMesh || root.isSkinnedMesh) {
    pushMeshMaterialEntries(root, root, entries, seen);
    return entries;
  }

  if (isShapeAsset(root) || isGlbAssetSelection(root) || isImageAsset(root)) {
    root.traverse?.((child) => {
      if (child.isMesh || child.isSkinnedMesh) {
        pushMeshMaterialEntries(child, root, entries, seen);
      }
    });
  }

  return entries;
}

const MATERIAL_LIBRARY_KEY = 'materialLibrary';

/** Materials unlinked from meshes but kept in the asset material list. */
export function getMaterialLibrary(root) {
  const assetRoot = findGlbMeshAssetRoot(root) || root;
  const list = assetRoot?.userData?.[MATERIAL_LIBRARY_KEY];
  return Array.isArray(list) ? list.filter((m) => m?.uuid) : [];
}

export function storeMaterialInLibrary(root, material) {
  if (!root || !material?.uuid) return;
  const assetRoot = findGlbMeshAssetRoot(root) || root;
  if (!assetRoot) return;
  assetRoot.userData = assetRoot.userData || {};
  const list = Array.isArray(assetRoot.userData[MATERIAL_LIBRARY_KEY])
    ? [...assetRoot.userData[MATERIAL_LIBRARY_KEY]]
    : [];
  if (!list.some((m) => m?.uuid === material.uuid)) {
    list.push(material);
  }
  assetRoot.userData[MATERIAL_LIBRARY_KEY] = list;
  assetRoot.userData.threeMaterialEdited = true;
}

export function removeMaterialFromLibrary(root, materialUuid) {
  if (!root || !materialUuid) return;
  const assetRoot = findGlbMeshAssetRoot(root) || root;
  if (!assetRoot?.userData?.[MATERIAL_LIBRARY_KEY]) return;
  assetRoot.userData[MATERIAL_LIBRARY_KEY] = (
    assetRoot.userData[MATERIAL_LIBRARY_KEY] || []
  ).filter((m) => m?.uuid !== materialUuid);
}

/**
 * Unique materials used anywhere under a GLB / shape / image asset.
 * Shared material instances appear once, with mesh usage count.
 * Also includes unused library materials (removed from object but kept in list).
 */
export function collectUniqueMaterialsInObject(root) {
  if (!root) return [];

  const assetRoot = findGlbMeshAssetRoot(root) || root;
  const byUuid = new Map();

  const consider = (mesh) => {
    if (!mesh?.isMesh && !mesh?.isSkinnedMesh) return;
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];

    materials.forEach((material, materialIndex) => {
      if (!material?.uuid) return;
      const existing = byUuid.get(material.uuid);
      if (existing) {
        existing.meshCount += 1;
        existing.isUnused = false;
        if (!existing.meshes.includes(mesh)) existing.meshes.push(mesh);
        if (!existing.sampleMesh) existing.sampleMesh = mesh;
        return;
      }

      const colorHex = material.color?.isColor
        ? `#${material.color.getHexString()}`
        : null;
      const name =
        (material.name && String(material.name).trim()) ||
        material.type ||
        `Material ${byUuid.size + 1}`;

      byUuid.set(material.uuid, {
        id: material.uuid,
        material,
        materialUuid: material.uuid,
        materialIndex,
        materialType: material.type || 'Material',
        name,
        colorHex,
        meshCount: 1,
        meshes: [mesh],
        sampleMesh: mesh,
        sampleLabel: getMeshPathLabel(mesh, assetRoot),
        isUnused: false,
      });
    });
  };

  if (assetRoot.isMesh || assetRoot.isSkinnedMesh) {
    consider(assetRoot);
  } else {
    assetRoot.traverse?.(consider);
  }

  // Keep materials removed from meshes as dummy/extra entries in the list.
  getMaterialLibrary(assetRoot).forEach((material) => {
    if (!material?.uuid || byUuid.has(material.uuid)) return;
    const colorHex = material.color?.isColor
      ? `#${material.color.getHexString()}`
      : null;
    const name =
      (material.name && String(material.name).trim()) ||
      material.type ||
      `Material ${byUuid.size + 1}`;
    byUuid.set(material.uuid, {
      id: material.uuid,
      material,
      materialUuid: material.uuid,
      materialIndex: 0,
      materialType: material.type || 'Material',
      name,
      colorHex,
      meshCount: 0,
      meshes: [],
      sampleMesh: null,
      sampleLabel: 'Unused',
      isUnused: true,
    });
  });

  return Array.from(byUuid.values());
}

/** True when every mesh in the selection uses the same material instance. */
export function selectionMeshesShareSingleMaterial(meshes) {
  if (!Array.isArray(meshes) || meshes.length === 0) return false;

  let sharedMat = null;
  for (const mesh of meshes) {
    if (!mesh?.isMesh && !mesh?.isSkinnedMesh) return false;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const primary = mats[0];
    if (!primary) return false;
    if (!sharedMat) sharedMat = primary;
    else if (primary !== sharedMat) return false;
  }
  return true;
}

export function applyPropertyOnEntry(entry, key, payload) {
  if (!entry?.material) return;

  if (
    payload?.kind === 'texture' &&
    payload.visibility !== undefined &&
    entry.mesh
  ) {
    // A material is shared intentionally: map visibility must update every
    // child that references the same instance, just like all other properties.
    setMaterialMapVisibility(entry.mesh, key, payload.visibility);
    markMaterialEdited(entry.mesh);
    markMeshesSharingMaterial(entry.mesh, entry.material);
    syncChildMaterialOverridesToAssetRoot(entry.mesh);
    syncMapScalarsToAssetRoot(entry.mesh, entry.material);
    entry.material.needsUpdate = true;
    return;
  }

  // Never clone a shared material here. Every property in the material
  // component (wireframe, maps, opacity, roughness, etc.) must propagate to
  // all child meshes that reference this material instance.
  applyMaterialPropertyValue(entry.material, key, payload);
  markMaterialEdited(entry.mesh);
  markMeshesSharingMaterial(entry.mesh, entry.material);
  syncChildMaterialOverridesToAssetRoot(entry.mesh);
  syncMapScalarsToAssetRoot(entry.mesh, entry.material);
}

/** Keep material_settings.mapScalars in sync so publish JSON restores intensity. */
function syncMapScalarsToAssetRoot(mesh, material) {
  const root = resolveMaterialAssetRoot(mesh);
  if (!root) return;
  const scalars = snapshotMapScalars(material);
  if (!scalars) return;
  if (!root.userData || Object.isFrozen(root.userData)) {
    root.userData = { ...(root.userData || {}) };
  }
  root.userData.material_settings = {
    ...(root.userData.material_settings || {}),
    mapScalars: scalars,
  };
}

/** Mark all meshes under the asset that use this material instance as edited. */
function markMeshesSharingMaterial(mesh, material) {
  if (!mesh || !material) return;
  const root = resolveMaterialAssetRoot(mesh);
  root?.traverse?.((obj) => {
    if (!obj.isMesh && !obj.isSkinnedMesh) return;
    const list = Array.isArray(obj.material) ? obj.material : [obj.material];
    if (list.some((m) => m === material)) {
      markMaterialEdited(obj);
    }
  });
}

export { SIDE_OPTIONS, BLENDING_OPTIONS };
